// Libs
import classNames from 'classnames/bind';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { TFunction } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseTable, ConfirmModal, Loading } from '~/components/common';
import { ProductVariantFormModal } from '~/components/module/inventory';
// Others
import { deleteVariant } from '~/thunks/variant/variantThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IHeaderCellType } from '~/utils/interface/common';
import { inventoryActions } from '~/thunks/inventory/inventorySlice';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { defaultValuesInventoryForm, schema } from '~/pages/common/inventories/inventory/inventoryForm/helper';
import { IDetailVariant, IFormInventory, IInventoryDetail, IItemVariant } from '~/utils/interface/inventory';
// Styles, images, icons
import styles from './ProductVariantDetail.module.scss';
import { icons } from '~/assets';

type Props = {
  detailProduct?: IInventoryDetail;
};

const cx = classNames.bind(styles);

const ProductVariantDetail = (props: Props) => {
  //#region Destructuring Props
  const { detailProduct } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const dispatch = useAppDispatch();

  const { control, watch, reset, getValues } = useForm<IFormInventory>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesInventoryForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);

  const [idVariant, setIdVariant] = useState<string>(EMPTY_STRING);
  const [dataVariant, setDataVariant] = useState<IItemVariant | null>();
  const [indexItem, setIndexItem] = useState<number>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!detailProduct) return;

    const { id, inventoryImages, inventoryFiles, addOns, variants, ...remainingData } = detailProduct;

    const dataConvert: IFormInventory = {
      ...remainingData,
      categoryId: detailProduct?.category?.id || EMPTY_STRING,
      variants: variants.map((item: IDetailVariant) => {
        return {
          id: String(item.id || EMPTY_STRING),
          name: String(item.name || EMPTY_STRING),
          length: String(item.length || EMPTY_STRING),
          color: String(item.color || EMPTY_STRING),
          size: String(item.size || EMPTY_STRING),
          height: String(item.height || EMPTY_STRING),
          weight: String(item.weight || EMPTY_STRING),
          style: String(item.style || EMPTY_STRING),
          description: String(item.description || EMPTY_STRING),
          quantity: String(item.quantity || EMPTY_STRING),
          rentalPrice: String(item.rentalPrice || EMPTY_STRING),
          variantImage: item.variantImage ? { id: item.fileId, url: item.variantImage } : {},
        };
      }),
    };

    reset(dataConvert);
  }, [JSON.stringify(detailProduct)]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    setIsShowModal(!isShowModal);
  };

  const handleDelete = (data: IItemVariant) => {
    if (!data) return;

    setIdVariant(String(data?.id));
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdVariant(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteVariant(idVariant))
      .unwrap()
      .then(() => {
        setIdVariant(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(inventoryActions.setRefreshInventoryDetail(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IItemVariant>[] => {
    return [
      {
        id: 'name',
        label: t('inventory_form_title_product_variants_name'),
        render: (record: IItemVariant) => record.name || EMPTY_VALUE,
        minWidth: 100,
      },
      {
        id: 'quantity',
        label: t('inventory_form_title_product_variants_quantity'),
        render: (record: IItemVariant) => record.quantity,
        minWidth: 100,
      },
      {
        id: 'price',
        label: t('inventory_form_title_product_variants_price'),
        render: (record: IItemVariant) => <>{formatCurrencyUSD({ value: record.rentalPrice, showDecimal: true })}</>,
        minWidth: 100,
      },
      {
        id: 'action',
        label: t('inventory_form_title_product_variants_action'),
        minWidth: 100,
        align: 'center',
        render: (record: IItemVariant, index: number) => {
          return (
            <Stack direction={'row'} gap={1}>
              <IconButton
                className={cx('buttonIcon')}
                onClick={() => {
                  setDataVariant(record);
                  setIndexItem(index);
                  setIsShowModal(true);
                }}
              >
                <img
                  className={cx('iconEdit')}
                  src={icons.tableIconActionEdit}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>

              <IconButton
                className={cx('buttonIcon')}
                onClick={() => {
                  handleDelete(record);
                }}
              >
                <img
                  className={cx('iconDelete')}
                  src={icons.commonIconTrash}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>
            </Stack>
          );
        },
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='productVariantDetailComponent' gap={6} className={cx('container')}>
      <FormProvider
        {...({
          control,
          watch,
        } as UseFormReturn<IFormInventory>)}
      >
        <Typography className={cx('textHeader')}>{t('inventory_detail_title_product_variants')}</Typography>

        <BaseTable columns={columnsItem(t)} dataSource={getValues()?.variants || []} />

        {isShowConfirmDelete && (
          <ConfirmModal
            type='delete'
            icon={icons.tableIconActionDelete}
            title={t('common_title_confirm_delete', { ns: 'common/base' })}
            textConfirm={t('common_action_delete', { ns: 'common/base' })}
            onClose={handleCloseConfirmDelete}
            onConfirm={handleConfirmDelete}
          />
        )}

        {isShowModal && (
          <ProductVariantFormModal
            isDetail
            onClose={handleCloseModal}
            dataVariant={dataVariant}
            indexItem={indexItem}
          />
        )}

        {isLoading && <Loading />}
      </FormProvider>
    </Stack>
  );
};

export default ProductVariantDetail;
